import React, { useEffect } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { AppText } from '../AppText';
import PantrySearchBar from './PantrySearchBar';
import PantrySpinner from './PantrySpinner';
import { useSiteMapRegionAndSites, SiteMapUser } from '../map/useSiteMapRegionAndSites';
import { useSiteSearch, UseSiteSearchResult } from '../map/useSiteSearch';
import { useSiteSelection } from '../map/useSiteSelection';
import { useSiteSearchTerm } from '../map/useSiteSearchTerm';
import { ForNeighborsError } from '../map/classifyForNeighborsError';
import {
  navigateToSiteDetails,
  resolveSiteDetailsDestination,
  NavigationLike,
} from '../map/navigateToSiteDetails';
import { SiteMap } from '../map/SiteMap';
import { SiteList } from '../map/SiteList';
import { SiteDetailsView } from '../map/SiteDetailsView';
import { SitePreviewCard } from '../map/SitePreviewCard';
import { Coordinate, Site } from '../../types/Site';
import distance from '../../utils/distance';
import { useTranslation } from '../../providers/TranslationProvider';
import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';

const recenterIconSource = require('../../assets/images/location_cross.png');

// Extracted purely to keep SiteMapPantriesContent's own complexity down (per Fallow's check) --
// narrow layout only: switching to the list tab hides the map (and its preview overlay), so clear
// the selection rather than leaving it to silently reappear if the user switches back.
function useDismissSelectionOnListTab(
  tabView: 'map' | 'list',
  selectedSite: Site | null,
  dismissSelection: () => void
) {
  useEffect(() => {
    if (Layout.usePantryTabs && tabView === 'list' && selectedSite) {
      dismissSelection();
    }
  }, [tabView, selectedSite, dismissSelection]);
}

const styles = StyleSheet.create({
  columnContainer: { flex: 1, flexDirection: 'column' },
  fill: { flex: 1 },
  visible: { display: 'flex' },
  hidden: { display: 'none' },
  rowContainer: { flex: 1, flexDirection: 'row' },
  mapContainer: { flex: 1, position: 'relative' },
  recenterButton: { position: 'absolute', bottom: 8, left: 8 },
  recenterIcon: { width: 40, height: 40 },
  // Absolutely positioned (like PantryMap.js's own pantryDetailsContainer) rather than a flex
  // sibling of the map/list, so its content never affects their layout.
  previewOverlay: { position: 'absolute', left: 0, right: 0, bottom: 0 },
  errorContainer: { flex: 1, padding: 32, alignItems: 'center', justifyContent: 'center' },
  errorTitle: { fontSize: 18, fontWeight: '600', textAlign: 'center' },
  errorMessage: { fontSize: 14, color: Colors.grayText, textAlign: 'center', marginTop: 8 },
  retryLink: { color: Colors.primaryColor, textDecorationLine: 'underline', marginTop: 16 },
});

// Narrow (Layout.usePantryTabs): map/list panels toggle by tabView, with a SitePreviewCard
// overlay for the selected site (mirrors PantryMap.js's pantryDetailsContainer preview).
function NarrowLayoutContent({
  tabView,
  mapView,
  listView,
  selectedSite,
  navigation,
  previewDistanceMiles,
}: {
  tabView: 'map' | 'list';
  mapView: React.ReactElement;
  listView: React.ReactElement;
  selectedSite: Site | null;
  navigation: NavigationLike;
  previewDistanceMiles: number | null;
}) {
  // Computed (not literal-inline) so react-native/no-inline-styles doesn't flag a style that
  // depends on tabView state and so can't be a static StyleSheet entry.
  const mapPanelStyle = [styles.fill, tabView === 'map' ? styles.visible : styles.hidden];
  const listPanelStyle = [styles.fill, tabView === 'list' ? styles.visible : styles.hidden];

  return (
    <View style={styles.fill}>
      <View testID="map-container" style={mapPanelStyle}>
        {mapView}
      </View>
      <View testID="list-container" style={listPanelStyle}>
        {listView}
      </View>
      {selectedSite && (
        <View style={styles.previewOverlay} testID="site-map-pantries-preview-overlay">
          <SitePreviewCard
            site={selectedSite}
            distanceMiles={previewDistanceMiles}
            onViewDetails={() => navigateToSiteDetails(navigation, selectedSite)}
          />
        </View>
      )}
    </View>
  );
}

// Wide: a legacy site's details show in PantriesScreen.jsx's Modal instead (via
// onSelectLegacyPantry), so the list panel stays put underneath it -- only a FreshTrak
// selection replaces it with the inline SiteDetailsView panel.
function WideLayoutContent({
  mapView,
  listView,
  selectedSite,
  isLoggedIn,
  dismissSelection,
  userCoordinate,
}: {
  mapView: React.ReactElement;
  listView: React.ReactElement;
  selectedSite: Site | null;
  isLoggedIn: boolean;
  dismissSelection: () => void;
  userCoordinate: Coordinate | null;
}) {
  const rightPanel =
    selectedSite && resolveSiteDetailsDestination(selectedSite).kind === 'freshTrakSite' ? (
      <View style={styles.fill} testID="site-map-pantries-details-panel">
        <SiteDetailsView
          siteId={selectedSite.siteId}
          isLoggedIn={isLoggedIn}
          onBack={dismissSelection}
          userCoordinate={userCoordinate ?? undefined}
        />
      </View>
    ) : (
      listView
    );

  return (
    <View style={styles.rowContainer}>
      {mapView}
      {rightPanel}
    </View>
  );
}

// Search results surface their own loading/error/resultsTruncated via SiteList (see
// useSiteSearch). Outside of search, this instead mirrors the legacy PantryList's own spinner +
// error-with-retry states, rather than leaving the gated user looking at an empty map and empty
// list with no feedback while the radius fetch is in flight or has failed (see
// useSitesWithinRadius for how `error` is classified).
function SiteListContent({
  isSearching,
  sites,
  activeQuery,
  searchState,
  loading,
  error,
  refetch,
  onSelectSite,
  t,
  translation,
}: {
  isSearching: boolean;
  sites: Site[];
  activeQuery: string;
  searchState: UseSiteSearchResult;
  loading: boolean;
  error: ForNeighborsError | null;
  refetch: () => void;
  onSelectSite: (site: Site) => void;
  t: (key: string) => string;
  translation: unknown;
}) {
  if (isSearching) {
    return (
      <SiteList
        sites={sites}
        onSelectSite={onSelectSite}
        loading={searchState.loading}
        error={searchState.error}
        resultsTruncated={searchState.resultsTruncated}
        query={activeQuery}
        onRetry={searchState.refetch}
      />
    );
  }
  if (error) {
    return (
      <View style={styles.errorContainer} testID="site-map-pantries-error">
        <AppText style={styles.errorTitle} testID="site-map-pantries-error-title">
          {t('site_map_load_error_title')}
        </AppText>
        <AppText style={styles.errorMessage} testID="site-map-pantries-error-message">
          {t('site_map_load_error_message')}
        </AppText>
        <AppText style={styles.retryLink} onPress={refetch} testID="site-map-pantries-retry">
          {t('site_map_retry')}
        </AppText>
      </View>
    );
  }
  if (loading && sites.length === 0) {
    return <PantrySpinner translation={translation} showHeader={false} />;
  }
  return <SiteList sites={sites} onSelectSite={onSelectSite} />;
}

export interface SiteMapPantriesContentProps {
  navigation: NavigationLike;
  registration: { user?: SiteMapUser & { id?: number } };
  route: { params?: { zip?: string } };
  // Owned by PantriesScreen.jsx's own tab bar -- this component only reads it, mirroring
  // PantryMap.js/PantryList.js, which are likewise driven by PantriesScreen.jsx's tabView state
  // rather than each managing their own.
  tabView: 'map' | 'list';
  // Wide layouts only: selecting a legacy (non-FreshTrak) site hands off to this instead of
  // navigating away, so PantriesScreen.jsx can show the same PantryDetails Modal the legacy path
  // uses -- covering just the map/list area, not the whole screen. See useSiteSelection.
  onSelectLegacyPantry?: (organizationId: string) => void;
  // Wide layouts only: called (in addition to clearing selectedSite) when a selection is
  // dismissed, e.g. by tapping the map background -- lets PantriesScreen.jsx close its
  // PantryDetails Modal in step, so a legacy site's marker un-highlights and its Modal closes
  // together rather than the Modal being stuck open. See useSiteSelection.
  onDismissLegacyPantry?: () => void;
}

// Plugs into PantriesScreen.jsx in place of PantryMap/PantryList when the
// new_site_map_with_freshtrak_enabled Statsig gate is on. Reuses PantriesScreen.jsx's own screen
// chrome (tab bar, layout wrapper, PantryDetails Modal) -- this component owns the
// map/list/search/selection content itself, mirroring PantryMap.js/PantriesScreen.jsx's own
// per-layout selection behavior:
// - Narrow (Layout.usePantryTabs): selecting a site shows a SitePreviewCard overlay (mirrors
//   PantryMap.js's pantryDetailsContainer preview), with a "View details" tap-through to the full
//   Pantry (legacy) or SiteDetails (FreshTrak) screen via navigateToSiteDetails.
// - Wide: selecting a FreshTrak site replaces the list panel with SiteDetailsView inline (mirrors
//   PantriesScreen.jsx's inline PantryDetails panel). A legacy site instead calls
//   onSelectLegacyPantry (see useSiteSelection), which PantriesScreen.jsx wires to its own
//   selectPantry/PantryDetails Modal -- the same one the legacy (gate-off) path already uses. Its
//   marker still highlights (selectedSite is set either way) and the list panel stays visible
//   underneath the Modal, rather than being replaced.
//
// Search: reuses the legacy PantrySearchBar component for the input itself, rendered once above
// the map+list (shared between both, like PantriesScreen.jsx's own legacy searchBar instance).
// searchTerm/activeQuery reproduce legacy PantriesScreen.jsx's handleSearchChange/displayMode
// split: a 3+ character query enters "search mode" (activeQuery set, radius fetch paused, map
// and list both switch to search results); a fully-cleared query exits it; 1-2 characters is a
// deliberate no-op so a transient backspace doesn't drop a confirmed search's results.
export function SiteMapPantriesContent({
  navigation,
  registration,
  route,
  tabView,
  onSelectLegacyPantry,
  onDismissLegacyPantry,
}: SiteMapPantriesContentProps) {
  const { t, translation } = useTranslation();
  const isLoggedIn = !!registration.user?.id;

  const { searchTerm, activeQuery, isSearching, handleSearchChange } = useSiteSearchTerm();

  const {
    mapRegion,
    setMapRegion,
    sites: radiusSites,
    loading,
    error,
    refetch,
    recenter,
    userCoordinate,
  } = useSiteMapRegionAndSites(registration.user, isLoggedIn, route.params?.zip, {
    enabled: !isSearching,
  });
  const searchState = useSiteSearch(activeQuery, userCoordinate, isLoggedIn);
  const sites = isSearching ? searchState.sites : radiusSites;

  const { selectedSite, handleSelectSite, handleSelectSiteFromList, dismissSelection } = useSiteSelection(
    navigation,
    onSelectLegacyPantry,
    onDismissLegacyPantry
  );

  // Every distance a neighbor sees is measured from their own resolved location, never from the
  // map: no Sites operation vends a neighbor-relative figure (GetSitesWithinRadius measures from
  // the search center, which useSitesWithinRadius drops; SearchAllSites and DescribeSite carry
  // none). Computed identically in explore and search mode -- so the preview card and the details
  // header it taps through to can never disagree -- and null when the location is unresolved, in
  // which case the card shows no distance rather than a fabricated one. Mirrors useSite.ts's
  // distanceMiles, which does the same for the details header.
  const previewDistanceMiles =
    userCoordinate && selectedSite
      ? distance(
          userCoordinate.latitude,
          userCoordinate.longitude,
          selectedSite.coordinate.latitude,
          selectedSite.coordinate.longitude
        )
      : null;

  // Mirrors PantryMap.js's useEffect(selectedPantryId): re-center the map on the site that was
  // just selected.
  useEffect(() => {
    if (selectedSite) {
      setMapRegion((region: any) =>
        region.center(selectedSite.coordinate.latitude, selectedSite.coordinate.longitude)
      );
    }
  }, [selectedSite, setMapRegion]);

  useDismissSelectionOnListTab(tabView, selectedSite, dismissSelection);

  // A selection made while exploring doesn't belong to the search results: on narrow, its preview
  // overlay would sit on top of results it may not even be in; on wide, its details panel would
  // keep hiding the list. Starting search fresh clears it.
  useEffect(() => {
    if (isSearching) {
      dismissSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to isSearching flipping, not to dismissSelection's identity or the selection it clears
  }, [isSearching]);

  const searchBar = (
    <PantrySearchBar translation={translation} searchTerm={searchTerm} onSearchChange={handleSearchChange} />
  );

  const mapView = (
    <View style={styles.mapContainer} testID="site-map-pantries-map-panel">
      <SiteMap
        region={mapRegion}
        sites={sites}
        selectedSiteId={selectedSite?.siteId ?? null}
        onRegionChangeComplete={setMapRegion}
        onMarkerPress={handleSelectSite}
        onPress={dismissSelection}
      />
      <TouchableOpacity
        style={styles.recenterButton}
        onPress={recenter}
        testID="site-map-pantries-recenter-button"
      >
        <Image style={styles.recenterIcon} source={recenterIconSource} />
      </TouchableOpacity>
    </View>
  );

  const listView = (
    <View style={styles.fill} testID="site-map-pantries-list-panel">
      <SiteListContent
        isSearching={isSearching}
        sites={sites}
        activeQuery={activeQuery}
        searchState={searchState}
        loading={loading}
        error={error}
        refetch={refetch}
        onSelectSite={handleSelectSiteFromList}
        t={t}
        translation={translation}
      />
    </View>
  );

  const content = Layout.usePantryTabs ? (
    <NarrowLayoutContent
      tabView={tabView}
      mapView={mapView}
      listView={listView}
      selectedSite={selectedSite}
      navigation={navigation}
      previewDistanceMiles={previewDistanceMiles}
    />
  ) : (
    <WideLayoutContent
      mapView={mapView}
      listView={listView}
      selectedSite={selectedSite}
      isLoggedIn={isLoggedIn}
      dismissSelection={dismissSelection}
      userCoordinate={userCoordinate}
    />
  );

  return (
    <View style={styles.columnContainer} testID="site-map-pantries-content">
      {searchBar}
      {content}
    </View>
  );
}
